import React from 'react';
import PageHeader from '../components/PageHeader';
import { PERMISSIONS } from '../config/permissions';

const modulePermissionDefs = [
  {
    key: PERMISSIONS.GROUP_DATA,
    title: 'Group data',
    body:
      'Submit ministry group data: Add Group Data (dynamic entry), the data entry form, and the thank-you confirmation. Bottom “Add Group Data” shortcut appears when this key is present.',
  },
  {
    key: PERMISSIONS.DASHBOARD,
    title: 'Reports & graphs',
    body: 'Reports Summary and Graphs — view summaries and chart views of submitted data.',
  },
  {
    key: PERMISSIONS.REVIEW,
    title: 'Ministry Lead Review',
    body: 'Open the Ministry Lead Review workflow for reviewing submissions.',
  },
  {
    key: PERMISSIONS.INVENTORY,
    title: 'Inventory',
    body: 'Submit inventory counts and view inventory history.',
  },
  {
    key: PERMISSIONS.STEP_STUDY,
    title: 'Step Study',
    body:
      'Step Study tracking: dashboard, create a cohort, and open cohort detail pages. Cohort visibility can be narrowed with stepStudyCohortIds (see below).',
  },
  {
    key: PERMISSIONS.SUMMIT_FEEDBACK,
    title: 'Summit Feedback',
    body:
      'Submit feedback on CR Annual Summit workshops and sessions for the current year — one entry per workshop attended, editable until an administrator captures the year. Admins manage summit years, view results, and generate AI insights from /admin/summit-feedback.',
  },
];

/**
 * Administrator-only reference: how access is assigned in Clerk and what each level means.
 */
const AdminAccessPage = () => {
  return (
    <div className="entry-page">
      <PageHeader title="Portal access & roles" section="admin" />
      <div className="admin-access-guide" style={{ maxWidth: 640, lineHeight: 1.55 }}>
        <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: '1.25rem' }}>
          This page is only visible to <strong>portal administrators</strong>. Use it as a reference when assigning access in the Clerk Dashboard.
        </p>

        <h2 style={{ fontSize: '1.05rem', margin: '1.5rem 0 0.5rem', color: 'var(--cr-text)' }}>
          Word to use: <strong>administrator</strong> (technical value: <code>admin</code>)
        </h2>
        <p style={{ margin: '0 0 1rem' }}>
          The highest access level is a <strong>portal administrator</strong>. In Clerk, that is set on the user&apos;s{' '}
          <strong>public metadata</strong> as <code>role</code> = <code>&quot;{PERMISSIONS.ADMIN}&quot;</code> (the word{' '}
          <strong>admin</strong> is the exact string the app checks). Do <strong>not</strong> put <code>admin</code> in the{' '}
          <code>permissions</code> array — use <code>role</code> only for this level.
        </p>
        <ul style={{ margin: '0 0 1rem', paddingLeft: '1.25rem' }}>
          <li>Administrators can open every area of the portal, including admin-only tools (invitations, meetings, outreach, Ministry Checkup administration, and this page).</li>
          <li>Only administrators can send <strong>Invite User</strong> invitations (per product rules).</li>
        </ul>

        <h2 style={{ fontSize: '1.05rem', margin: '1.5rem 0 0.5rem', color: 'var(--cr-text)' }}>
          Module permission keys (<code>publicMetadata.permissions</code>)
        </h2>
        <p style={{ margin: '0 0 1rem' }}>
          Non-administrators get an array of short strings on <code>publicMetadata.permissions</code>. Each key below is defined in the app as a permission constant and unlocks the listed areas.
        </p>
        <dl style={{ margin: '0 0 1rem' }}>
          {modulePermissionDefs.map(({ key, title, body }) => (
            <div key={key} style={{ marginBottom: '1rem' }}>
              <dt style={{ margin: 0, fontWeight: 600 }}>
                <code>{key}</code> — {title}
              </dt>
              <dd style={{ margin: '0.35rem 0 0', paddingLeft: 0 }}>{body}</dd>
            </div>
          ))}
        </dl>

        <h2 style={{ fontSize: '1.05rem', margin: '1.5rem 0 0.5rem', color: 'var(--cr-text)' }}>
          Everyone else: <strong>permissions</strong> (combine as needed)
        </h2>
        <p style={{ margin: '0 0 1rem' }}>
          Users who are <strong>not</strong> administrators only get routes that match the keys you assign. Example metadata:
        </p>
        <pre
          style={{
            background: 'var(--cr-bg-warm)',
            border: '1px solid var(--cr-border)',
            borderRadius: 8,
            padding: '0.85rem 1rem',
            fontSize: '0.8rem',
            overflow: 'auto',
            margin: '0 0 1rem',
          }}
        >
{`{
  "permissions": [
    "${PERMISSIONS.GROUP_DATA}",
    "${PERMISSIONS.DASHBOARD}",
    "${PERMISSIONS.REVIEW}",
    "${PERMISSIONS.INVENTORY}",
    "${PERMISSIONS.STEP_STUDY}",
    "${PERMISSIONS.SUMMIT_FEEDBACK}"
  ]
}`}
        </pre>
        <p style={{ margin: '0 0 1rem' }}>
          Users only see the sections and tools they were given. You can assign one key or several.
        </p>

        <h2 style={{ fontSize: '1.05rem', margin: '1.5rem 0 0.5rem', color: 'var(--cr-text)' }}>
          Signed in, no module key required
        </h2>
        <p style={{ margin: '0 0 1rem' }}>
          Some screens only require a signed-in account — no entry in <code>permissions</code>. These include <strong>Home</strong>, <strong>My Survey</strong>, <strong>Help Videos</strong>, opening a{' '}
          <strong>Ministry Checkup</strong> survey from the list, and taking a survey from a link. Creating a new checkup and viewing completion or results are limited to{' '}
          <strong>administrators</strong>. The Ministry Checkup link under Leader &amp; Servant Development is shown to administrators, and to anyone with a checkup assigned to them.
        </p>

        <h2 style={{ fontSize: '1.05rem', margin: '1.5rem 0 0.5rem', color: 'var(--cr-text)' }}>
          Public help pages (no sign-in)
        </h2>
        <p style={{ margin: '0 0 1rem' }}>
          <strong>FAQs</strong>, <strong>Accessibility</strong>, and <strong>How to</strong> are available without signing in.
        </p>

        <h2 style={{ fontSize: '1.05rem', margin: '1.5rem 0 0.5rem', color: 'var(--cr-text)' }}>
          Step Study cohort access (optional)
        </h2>
        <p style={{ margin: '0 0 1rem' }}>
          For Step Study, you can also set <code>stepStudyCohortIds</code> so a leader only sees certain cohorts. Administrators are not limited by that list.
        </p>

        <p style={{ margin: '1.5rem 0 0', fontSize: '0.9rem', color: 'var(--cr-text-muted)' }}>
          Changes to metadata apply the next time the user loads the portal. For Clerk setup details, see your team&apos;s internal runbook or Clerk documentation.
        </p>
      </div>
    </div>
  );
};

export default AdminAccessPage;
